import React, { useRef, useState, useCallback } from 'react'
import SearchBar from '../../../../common/searchbar/SearchBar'
import Sort from '../../../../common/sort/Sort'
import ManageColumns from '../../../../common/managecolumns/ManageColumns'
import { SortColumn } from '../../../../common/managecolumns/SortColumn'
import CollapsableButton from '../../../../common/button/CollapsableButton'
import Button from '../../../../common/button/Button'
import Icons from '../../../../common/icons/Icons'
import ReactToPrint from 'react-to-print'
import {ExportPdf} from '../../ReportingGroup/Pdf/ExportPdf'
import DateRange from './DateRange';
import GetContext from '../../ReportingGroup/context/GetContext'
import { getFileName } from '../../ReportingGroup/utils'
import { generateCSV } from '../../ReportingGroup/csv/CSVExport'

const ReportingFilters = ({
  globalFilter,
  setGlobalFilter,
  setSorting,
  table,
  columnOrder,
  setColumnOrder,
  selectedLength,
  rowSelection,
  data,
  exportHeaders,
  columnVisibility,
  filterType='full-filter',
  from,
  context
}) => {
  const contentToPrint = useRef(null)
  const [loading, setLoading] = useState(false)
  const [dateValue, setDateValue] = useState('')
  const allowedFrom = ['Course Report', 'User Report']
  const exportTitle = selectedLength > 0 ? `Export Selected (${selectedLength})` : 'Export'
  const { siteOption, teamData } = GetContext( context )

  const fileName = getFileName( siteOption, teamData.team_name )

  const handleDateRange = () => {
    document.querySelector('.msync-group-dashboard-search-and-date .tf-button-action').click()
  }

  const setFilterOption = (from) => {
    switch (from) {
      case "Course Report":
      case "Enrolled Courses":
        return { asc: 'Course A-Z', desc: 'Course Z-A' }
      default:
        return null // return the default options
    }
  }

  const sortDataByColumnOrder = ( obj, columnOrder ) => {
    const reorderedObject = {};
    columnOrder.forEach(key => {
        if (key in obj) {
          reorderedObject[key] = obj[key];
        }
    });
    return reorderedObject;
  }

  const asyncData = () => {
    // in the future, call rest here if all rows are selected.
    const filteredData = data?.filter((_, index) => rowSelection[index.toString()])
    const reorderedData = filteredData?.map(obj => sortDataByColumnOrder( obj, columnOrder ));
    return reorderedData
  }

  const sortHeaderColumnByColumnOrder = ( headerColumn, columnOrder ) => {
    const reorderedObject = [];
    let index = 0;
    columnOrder.forEach((key) => {
      const value = headerColumn.find(item => item.id === key);
      if (value) {
        reorderedObject[index] = value;
        index++
      }
    });
    return reorderedObject;
  }

  const newExportHeaders = () => {
    // export all headers if all visible
    if (!Object.keys(columnVisibility).length || Object.values(columnVisibility).every(fn => fn === true)) {
      const reorderedColumns = sortHeaderColumnByColumnOrder( exportHeaders, columnOrder )
      return reorderedColumns
    }

    const toRemoveColumns = Object.entries(columnVisibility)
      .filter(([columnName, isVisible]) => !isVisible)
      .map(([columnName]) => columnName)

    const filteredColumns = exportHeaders?.filter(column => !toRemoveColumns.includes(column.id))
    const reorderedColumns = sortHeaderColumnByColumnOrder( filteredColumns, columnOrder )
    return reorderedColumns
  }

  const handleOnBeforeGetContent = useCallback(() => {
    setLoading(true)

    return new Promise((resolve) => {
      setTimeout(() => {
        setLoading(false)
        resolve()
      }, 700)
    })
  }, [])

  const reactToPrintContent = useCallback(() => {
    return contentToPrint.current
  }, [ contentToPrint.current ])

  const reactToPrintTrigger = useCallback(() => {

    return (
      <button type='button' className="msync-button msync-button--is-style-link msync-button--is-full-width msync-export-button" title='Ready to export the selected rows to PDF.'>
        <Icons type='pdf' />
        <span>Export as PDF</span>
      </button>
    )
  }, [])

  const disabledExportButtons = () =>{
    return(
      <>
        <button type='button' className="msync-button msync-button--is-style-link msync-button--is-full-width msync-export-button" title='Please select at least one row before exporting to PDF.' disabled>
          <Icons type='pdf' />
          <span>Export as PDF</span>
        </button>
        <button type='button' className="msync-button msync-button--is-style-link msync-button--is-full-width msync-export-button" title='Please select at least one row before exporting to CSV.' disabled>
          <Icons type='csv' />
          <span>Export as CSV</span>
        </button>
      </>
    )
  }

  return (
    <>
      {
        from !== 'Quizzes Report' && (
          <div style={{ display: 'none' }}>
            <ExportPdf
              ref={contentToPrint}
              from={from}
              columns={newExportHeaders()}
              datum={asyncData()}
              context={context}
              />
          </div>
      )
    }
      <div className="msync-table__header msync-report-header">
        <div className='msync-table__header__part msync-group-dashboard-search-and-date'>
          {allowedFrom.includes(from) && (
            <Button onClick={() => handleDateRange()}>
              <span>
                <Icons type='date' />
                {dateValue ?? 'Select Date Range'}
              </span>
              <DateRange
                from={from}
                setDateValue={setDateValue}
                allowedFrom={allowedFrom}
              />
            </Button>
          )}
          <SearchBar
            globalFilter={globalFilter}
            setGlobalFilter={setGlobalFilter}
          />
        </div>
        <div className="msync-table__header__part msync-group-dashboard-filters">
          {filterType === 'full-filter' && (
            <>
              <Sort
                setSorting={setSorting}
                columnName="course" // what column to filter
                setSortOption={setFilterOption(from)}
              />
              <CollapsableButton
                className={'msync-dropdown--is-align-end'}
                collapsed={false}
                icon={'table-column'}
                title={'Manage Columns'}
                heightWidth={15}
                showPanelHeader={true}
              >
                <div className="msync-dropdown__panel__body msync-dropdown__panel__body--2col">
                  <div className="msync-dropdown__panel__body__column">
                    <strong>Show / Hide Columns</strong>
                    <ManageColumns table={table} />
                  </div>
                  <div className="msync-dropdown__panel__body__column">
                    <strong>Reorder Columns</strong>
                    <SortColumn setColumnOrder={setColumnOrder} table={table} />
                  </div>
                </div>
              </CollapsableButton>
            </>
          )}
          <CollapsableButton
            className={'msync-dropdown--is-align-end'}
            collapsed={false}
            icon={'download'}
            heightWidth={12}
            title={exportTitle}
          >
            <div className="msync-dropdown__panel__button-menu msync-manage-columns-content msync-export-content">
              {
                loading ? <span>Exporting...</span> : 
                selectedLength ? 
                ( <>
                  <ReactToPrint
                    documentTitle={fileName}
                    content={reactToPrintContent}
                    onBeforeGetContent={handleOnBeforeGetContent}
                    removeAfterPrint
                    trigger={reactToPrintTrigger}
                    />
                  <Button
                    className="msync-button msync-button--is-style-link msync-button--is-full-width msync-export-button"
                    onClick={ () => generateCSV( newExportHeaders(), asyncData(), fileName ) }
                    title='Ready to export the selected rows to CSV.'
                  >
                    <Icons type='csv' />
                    <span>Export as CSV</span>
                  </Button>
                </> ) :
              disabledExportButtons()
            }
            </div>
          </CollapsableButton>
        </div>
      </div>
    </>
  )
}

export default ReportingFilters
